import {Box, HStack} from 'platform/foundation';
import styled, {css} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useCallback, useEffect, useRef} from 'react';

import {always} from 'ramda';
import {isTrue, isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {useAnimatedPopper} from '../AnimatedPopper/hooks/useAnimatedPopper';
import {DatePickerInput} from '../DatePicker/components/DatePickerInput';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {AutoScrollConfig, ScrollOperation} from './hooks/useTimePickerAutoScroll';
import {
  useTimePickerKeyboardNavigation,
  KeyboardNavigationConfig,
} from './hooks/useTimePickerKeyboardNavigation';
import {Hour} from './types/Hour';
import {Minute} from './types/Minute';
import {TimeType} from './types/TimeType';
import {getDropdownTimes} from './utils/getDropdownTimes';
import {getHoursAndMinutesFromTime} from './utils/getHoursAndMinutesFromTime';
import {getInputValue} from './utils/getInputValue';
import {isHourDisabled} from './utils/isHourDisabled';
import {isMinuteDisabled} from './utils/isMinuteDisabled';

export interface TimePickerProps extends FormControlProps<TimeType | null>, TestIdProps {
  /**
   * @about Minute interval for the dropdown. Defaults to 1.
   */
  step?: 1 | 5 | 10 | 15 | 30;
  /**
   * @about Minimum time that can be selected. Defaults to 00:00.
   */
  minTime?: TimeType;
  /**
   * @about Maximum time that can be selected. Defaults to 23:59.
   */
  maxTime?: TimeType;
  placeholder?: string;
  isRecommended?: boolean;
  /**
   * @about Enable keyboard navigation for the TimePicker. Defaults to true.
   */
  keyboardNavigationEnabled?: boolean;
  /**
   * @about Enable focus trap within the dropdown. Defaults to true.
   */
  focusTrapEnabled?: boolean;
  /**
   * @about Configuration for keyboard navigation behavior.
   */
  keyboardConfig?: Partial<KeyboardNavigationConfig>;
  /**
   * @about Enable auto-scroll for keyboard navigation. Defaults to true.
   */
  autoScrollEnabled?: boolean;
  /**
   * @about Configuration for auto-scroll behavior.
   */
  autoScrollConfig?: Partial<AutoScrollConfig>;
  /**
   * @about Callback fired when auto-scroll starts.
   */
  onScrollStart?: (operation: ScrollOperation) => void;
  /**
   * @about Callback fired when auto-scroll completes.
   */
  onScrollComplete?: (operation: ScrollOperation) => void;
  /**
   * @about Enable non-clearable mode. Defaults to false.
   */
  isNonClearable?: boolean;
}

export function TimePicker(props: TimePickerProps) {
  const {popperProps, Popper, openPopper, closePopper, referenceRef, isOpen} = useAnimatedPopper();

  const t = useTranslationContext();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Refs for auto-scroll functionality
  const hourElementsRef = useRef<HTMLElement[]>([]);
  const minuteElementsRef = useRef<HTMLElement[]>([]);

  // Ref callback functions to populate element arrays
  const setHourElementRef = useCallback((element: HTMLElement | null, index: number) => {
    if (element) {
      hourElementsRef.current[index] = element;
    }
  }, []);

  const setMinuteElementRef = useCallback((element: HTMLElement | null, index: number) => {
    if (element) {
      minuteElementsRef.current[index] = element;
    }
  }, []);

  const {hours, minutes} = getDropdownTimes(props.step);

  const {hour: selectedHour, minute: selectedMinute} = getHoursAndMinutesFromTime(props.value) || {
    hour: null,
    minute: null,
  };

  const {hour: minHour, minute: minMinute} = getHoursAndMinutesFromTime(props.minTime) || {
    hour: '00',
    minute: '00',
  };
  const {hour: maxHour, minute: maxMinute} = getHoursAndMinutesFromTime(props.maxTime) || {
    hour: '23',
    minute: '59',
  };

  // Helper functions for keyboard navigation
  const isHourDisabledForKeyboard = (hour: Hour) => isHourDisabled(hour, minHour, maxHour);
  const isMinuteDisabledForKeyboard = (minute: Minute) =>
    isMinuteDisabled(selectedHour, minute, minHour, minMinute, maxHour, maxMinute);

  useEffect(() => {
    if (isOpen) {
      hourRef.current?.scrollIntoView({block: 'center'});
      minuteRef.current?.scrollIntoView({block: 'center'});
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      (selectedHour === minHour && selectedMinute === minMinute) ||
      (selectedHour === maxHour && selectedMinute === maxMinute)
    ) {
      minuteRef.current?.scrollIntoView({block: 'center'});
    }
  }, [maxHour, maxMinute, minHour, minMinute, selectedHour, selectedMinute]);

  const onHourSelect = (hour: Hour) => {
    let minute = selectedMinute || '00';

    if (hour === minHour && minute < minMinute) {
      minute = minMinute;
    } else if (hour === maxHour && minute > maxMinute) {
      minute = maxMinute;
    }

    const time: TimeType = `${hour}:${minute}`;
    props.onChange?.(time);
  };

  const onMinuteSelect = (minute: Minute) => {
    if (!selectedHour) {
      return;
    }

    const time: TimeType = `${selectedHour}:${minute}`;
    props.onChange?.(time);
    closePopper();
  };

  // Keyboard navigation integration
  const keyboardNavigation = useTimePickerKeyboardNavigation({
    hours,
    minutes,
    selectedHour,
    selectedMinute,
    isOpen,
    onHourSelect: (hour: Hour, focusNext?: boolean) => {
      onHourSelect(hour);
      if (focusNext && props.keyboardNavigationEnabled !== false) {
        // The hook will handle moving focus to minutes section
      }
    },
    onMinuteSelect,
    onClose: closePopper,
    onOpen: openPopper,
    isDisabled: props.isDisabled,
    isHourDisabled: isHourDisabledForKeyboard,
    isMinuteDisabled: isMinuteDisabledForKeyboard,
    dropdownRef,
    inputRef,
    hourElements: hourElementsRef.current,
    minuteElements: minuteElementsRef.current,
    onScrollStart: props.onScrollStart,
    onScrollComplete: props.onScrollComplete,
    config: {
      enabled: props.keyboardNavigationEnabled !== false,
      focusTrapEnabled: props.focusTrapEnabled !== false,
      autoScrollEnabled: props.autoScrollEnabled,
      autoScrollConfig: props.autoScrollConfig,
      ...props.keyboardConfig,
    },
  });

  return (
    <>
      <div
        ref={referenceRef}
        css={css`
          position: relative;
        `}
      >
        <DatePickerInput
          ref={inputRef}
          leftIcon="devices/access_time"
          onClick={props.isDisabled ? undefined : openPopper}
          onFocus={
            props.isDisabled
              ? undefined
              : () => {
                  if (!isOpen) {
                    openPopper();
                    // Auto-focus hour list after dropdown opens and blur input
                    setTimeout(() => {
                      // Force blur the input to remove cursor - try multiple approaches
                      if (inputRef.current) {
                        inputRef.current.blur();
                      }
                      // Also blur whatever is currently focused
                      if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                      }

                      // Small additional delay to ensure blur completes
                      setTimeout(() => {
                        // Focus the dropdown container to capture keyboard events
                        if (dropdownRef.current) {
                          dropdownRef.current.focus();
                        }
                        // Activate keyboard navigation
                        keyboardNavigation.moveFocusToSection('hours');
                      }, 10);
                    }, 100); // Increased timeout to ensure popper is fully rendered
                  }
                }
          }
          onKeyDown={keyboardNavigation.handleInputKeyDown}
          isRequired={props.isRequired}
          isDisabled={props.isDisabled}
          value={getInputValue(selectedHour, selectedMinute)}
          placeholder={match([isNotNilOrEmpty(props.placeholder), isTrue(props.isDisabled)])
            .with([true, Pattern.boolean], always(props.placeholder))
            .with([false, true], always(null))
            .otherwise(always(t('general.labels.select')))}
          label={props.label}
          tooltip={props.tooltip}
          isInvalid={isInvalid}
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('input', props)}
          onClearInput={props.isNonClearable ? undefined : () => props.onChange?.(null)}
        />
      </div>
      <Popper {...popperProps}>
        <Box paddingTop={1}>
          <Box
            borderRadius="medium"
            boxShadow="elevation_2"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            backgroundColor="general.white"
            data-testid={suffixTestId('dropdown', props)}
          >
            <div
              ref={dropdownRef}
              onKeyDown={keyboardNavigation.handleKeyDown}
              tabIndex={-1}
              role="listbox"
              aria-label="Time picker options"
              css={css`
                outline: none;
              `}
            >
              <HStack>
                <List
                  data-testid={suffixTestId('hourList', props)}
                  role="listbox"
                  aria-label="Hours"
                >
                  {hours.map((hour, index) => {
                    const isDisabled = isHourDisabled(hour, minHour, maxHour);
                    const focusProps = keyboardNavigation.getFocusProps('hours', index);
                    return (
                      <Selection
                        $isDisabled={isDisabled}
                        $isSelected={hour === selectedHour}
                        $isFocused={focusProps.isFocused}
                        ref={(element) => {
                          // Set ref for selected item (existing functionality)
                          if (hour === selectedHour || hour === minHour) {
                            if (hourRef.current !== element) {
                              hourRef.current = element;
                            }
                          }
                          // Set ref for auto-scroll functionality
                          setHourElementRef(element, index);
                        }}
                        onClick={() => {
                          if (isDisabled) {
                            return;
                          }
                          keyboardNavigation.setFocusedItem('hours', index);
                          onHourSelect(hour);
                        }}
                        tabIndex={focusProps.tabIndex}
                        role="option"
                        aria-selected={hour === selectedHour}
                        key={hour}
                      >
                        {hour}
                      </Selection>
                    );
                  })}
                </List>
                <List
                  data-testid={suffixTestId('minuteList', props)}
                  role="listbox"
                  aria-label="Minutes"
                >
                  {minutes.map((minute, index) => {
                    const isDisabled = isMinuteDisabled(
                      selectedHour,
                      minute,
                      minHour,
                      minMinute,
                      maxHour,
                      maxMinute
                    );
                    const focusProps = keyboardNavigation.getFocusProps('minutes', index);
                    return (
                      <Selection
                        $isDisabled={isDisabled}
                        $isSelected={minute === selectedMinute}
                        $isFocused={focusProps.isFocused}
                        ref={(element) => {
                          // Set ref for selected item (existing functionality)
                          if (minute === selectedMinute) {
                            if (minuteRef.current !== element) {
                              minuteRef.current = element;
                            }
                          }
                          // Set ref for auto-scroll functionality
                          setMinuteElementRef(element, index);
                        }}
                        onClick={() => {
                          if (isDisabled) {
                            return;
                          }
                          keyboardNavigation.setFocusedItem('minutes', index);
                          onMinuteSelect(minute);
                        }}
                        tabIndex={focusProps.tabIndex}
                        role="option"
                        aria-selected={minute === selectedMinute}
                        key={minute}
                      >
                        {minute}
                      </Selection>
                    );
                  })}
                </List>
              </HStack>
            </div>
          </Box>
        </Box>
      </Popper>
    </>
  );
}

interface SelectionProps {
  $isSelected?: boolean;
  $isDisabled?: boolean;
  $isFocused?: boolean;
}

const Selection = styled.div<SelectionProps>`
  text-align: center;
  white-space: nowrap;
  width: ${({theme}) => theme.getSize(12)};
  border-radius: ${({theme}) => theme.radii.small};
  padding-top: ${({theme}) => theme.getSize(2)};
  padding-bottom: ${({theme}) => theme.getSize(2)};
  cursor: ${({$isDisabled}) => ($isDisabled ? 'not-allowed' : 'pointer')};
  background-color: ${({$isSelected, theme}) =>
    $isSelected ? theme.colors.palettes.blue[10][100] : 'initial'};
  color: ${({theme, $isSelected, $isDisabled}) => {
    if ($isDisabled) {
      return theme.colors.palettes.neutral[900][40];
    }
    if ($isSelected) {
      return theme.colors.palettes.blue[80][100];
    }
    return theme.colors.text.primary;
  }};
  &:hover {
    background-color: ${({theme, $isDisabled}) =>
      !$isDisabled && theme.colors.palettes.blue[10][100]};
    color: ${({theme, $isDisabled}) => !$isDisabled && theme.colors.palettes.blue[80][100]};
  }

  ${({$isFocused, theme}) =>
    $isFocused &&
    css`
      outline: 2px solid ${theme.colors.palettes.blue[60][100]};
      outline-offset: 1px;
      background-color: ${theme.colors.palettes.blue[10][100]};
    `}
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  scrollbar-gutter: stable;
  overflow-y: auto;
  height: ${({theme}) => theme.getSize(60)};
  &:hover {
    overflow-y: auto;
  }
  padding: ${({theme}) => theme.getSize(4)};
  width: 100%;
`;
